import { useToast } from 'primevue/usetoast'
import { env } from '@/utils/env.js'

/**
 * Toast Utility
 * Provides standardized toast notifications with consistent styling and behavior
 */
class ToastService {
  constructor() {
    this.toast = null
    this.defaultOptions = {
      life: 3000, // 3 seconds default
      closable: true
    }
  }

  /**
   * Initialize toast service (should be called in components)
   */
  init() {
    if (!this.toast) {
      this.toast = useToast()
    }
    return this
  }

  /**
   * Show success toast
   * @param {string|object} message - Message string or options object
   * @param {string} summary - Optional summary (if message is string)
   * @param {object} options - Optional additional options
   */
  success(message, summary = 'Success', options = {}) {
    const toastOptions = this._buildOptions('success', message, summary, options.life || this.defaultOptions.life)
    this._show({ ...toastOptions, ...options })
    env.log('Toast Success:', toastOptions.detail)
  }

  /**
   * Show error/danger toast
   * @param {string|object} message - Message string or options object
   * @param {string} summary - Optional summary (if message is string)
   * @param {object} options - Optional additional options
   */
  error(message, summary = 'Error', options = {}) {
    const toastOptions = this._buildOptions('error', message, summary, options.life || 5000)
    this._show({ ...toastOptions, ...options })
    env.error('Toast Error:', toastOptions.detail)
  }

  /**
   * Show warning toast
   * @param {string|object} message - Message string or options object
   * @param {string} summary - Optional summary (if message is string)
   * @param {object} options - Optional additional options
   */
  warning(message, summary = 'Warning', options = {}) {
    const toastOptions = this._buildOptions('warn', message, summary, options.life || 4000)
    this._show({ ...toastOptions, ...options })
    env.warn('Toast Warning:', toastOptions.detail)
  }

  /**
   * Show info toast
   * @param {string|object} message - Message string or options object
   * @param {string} summary - Optional summary (if message is string)
   * @param {object} options - Optional additional options
   */
  info(message, summary = 'Info', options = {}) {
    const toastOptions = this._buildOptions('info', message, summary, options.life || this.defaultOptions.life)
    this._show({ ...toastOptions, ...options })
    env.debug('Toast Info:', toastOptions.detail)
  }

  /**
   * Show loading toast (typically for ongoing operations)
   * @param {string|object} message - Message string or options object
   * @param {string} summary - Optional summary (if message is string)
   */
  loading(message, summary = 'Loading', options = {}) {
    const toastOptions = this._buildOptions('info', message, summary, 0) // 0 = no auto-close
    toastOptions.closable = false
    this._show({ ...toastOptions, ...options })
    env.debug('Toast Loading:', toastOptions.detail)
    return toastOptions // Return so it can be closed manually
  }

  /**
   * Close all toasts
   */
  clear() {
    if (this.toast) {
      this.toast.removeAllGroups()
    }
  }

  /**
   * Close specific toast group
   * @param {string} group - Toast group name
   */
  clearGroup(group) {
    if (this.toast) {
      this.toast.removeGroup(group)
    }
  }

  /**
   * Specialized toast methods for common scenarios
   */

  /**
   * Save success notification
   * @param {string} entityName - Name of saved entity (e.g., 'Permission', 'User')
   */
  saveSuccess(entityName = 'Item') {
    this.success(`${entityName} saved successfully!`, 'Saved')
  }

  /**
   * Delete success notification
   * @param {string} entityName - Name of deleted entity
   */
  deleteSuccess(entityName = 'Item') {
    this.success(`${entityName} deleted successfully!`, 'Deleted')
  }

  /**
   * Update success notification
   * @param {string} entityName - Name of updated entity
   */
  updateSuccess(entityName = 'Item') {
    this.success(`${entityName} updated successfully!`, 'Updated')
  }

  /**
   * Network error notification
   */
  networkError() {
    this.error('Network error. Please check your connection and try again.', 'Connection Error')
  }

  /**
   * Validation error notification
   * @param {string|array} errors - Validation error messages
   */
  validationError(errors = 'Please check your input and try again.') {
    let message = errors
    if (Array.isArray(errors)) {
      message = errors.join(', ')
    } else if (typeof errors === 'object') {
      message = Object.values(errors).flat().join(', ')
    }
    this.error(message, 'Validation Error')
  }

  /**
   * Permission denied notification
   */
  permissionDenied() {
    this.warning('You do not have permission to perform this action.', 'Access Denied')
  }

  /**
   * Operation cancelled notification
   */
  cancelled(operation = 'Operation') {
    this.info(`${operation} cancelled.`, 'Cancelled')
  }

  /**
   * Login success notification
   */
  loginSuccess(userName = '') {
    const message = userName ? `Welcome back, ${userName}!` : 'Login successful!'
    this.success(message, 'Welcome')
  }

  /**
   * Logout notification
   */
  logout() {
    this.info('You have been logged out successfully.', 'Goodbye')
  }

  /**
   * Copy to clipboard notification
   */
  copied(item = 'Text') {
    this.success(`${item} copied to clipboard!`, 'Copied', { duration: 2000 })
  }

  /**
   * Private helper methods
   */

  /**
   * Build toast options object
   * @private
   */
  _buildOptions(severity, message, summary, life) {
    // If message is already an options object
    if (typeof message === 'object' && message !== null) {
      return {
        severity,
        life: this.defaultOptions.life,
        closable: this.defaultOptions.closable,
        ...message // Override with provided options
      }
    }

    // Build options from parameters
    return {
      severity,
      summary,
      detail: message,
      life,
      closable: this.defaultOptions.closable
    }
  }

  /**
   * Show toast notification
   * @private
   */
  _show(options) {
    if (!this.toast) {
      
      return
    }

    this.toast.add(options)
  }
}

// Create singleton instance
const toastService = new ToastService()

/**
 * Composable for using toast notifications
 * Usage in components:
 * 
 * import { useNotifications } from '@/utils/toast.js'
 * 
 * const toast = useNotifications()
 * toast.success('Operation completed!')
 * toast.error('Something went wrong')
 * toast.saveSuccess('Permission')
 */
export const useNotifications = () => {
  return toastService.init()
}

/**
 * Direct export of toast service for non-composable usage
 */
export { toastService as toast }

/**
 * Quick access functions for global usage
 */
export const showSuccess = (message, summary, options) => {
  toastService.init().success(message, summary, options)
}

export const showError = (message, summary, options) => {
  toastService.init().error(message, summary, options)
}

export const showWarning = (message, summary, options) => {
  toastService.init().warning(message, summary, options)
}

export const showInfo = (message, summary, options) => {
  toastService.init().info(message, summary, options)
}

/**
 * Check if an error is a permission/authorization error (HTTP 403).
 * Works with both raw Axios errors and errors formatted by BaseApiService.handleError.
 * 
 * Usage in catch blocks:
 *   } catch (error) {
 *     if (!isPermissionError(error)) {
 *       toast.add({ severity: 'error', ... })
 *     }
 *   }
 */
export const isPermissionError = (error) => {
  return (
    error?._permissionDenied === true ||
    error?.response?.status === 403 ||
    error?.status === 403
  )
}

// Export default for convenience
export default toastService
